/**
 * PromotionGateCard — the agent→code (rung ④) gate (UI-SPEC §4). Shows the
 * from→to rung, the synthesized script, the match %/savings, and the live drift.
 * Approve/Reject/Inspect actions are injected by the page (client island) so the
 * card is reused read-only in Workflow Studio and interactively in the Verdict Queue.
 */

import * as React from "react";
import { Card, CardContent } from "../../card";
import { StatusBadge } from "../status-badge";
import { RungBadge } from "./rung-badge";
import { ArrowRight } from "../../icons";

export interface PromotionGateItem {
  id: number;
  step_name: string;
  from_rung: number;
  to_rung: number;
  artifact_path: string | null;
  artifact_hash: string | null;
  state: "proposed" | "shadow" | "active" | "demoted" | "rolled_back";
  drift: number | null;
}

export function PromotionGateCard({
  promotion,
  savingsLabel,
  actions,
}: {
  promotion: PromotionGateItem;
  savingsLabel?: string;
  /** Approve/Reject/Inspect buttons (client island). */
  actions?: React.ReactNode;
}) {
  return (
    <Card className="py-0">
      <CardContent className="flex flex-col gap-2 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="type-body font-medium text-foreground">▣ Promote step “{promotion.step_name}”</span>
          <RungBadge rung={promotion.from_rung} />
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <RungBadge rung={promotion.to_rung} />
          <StatusBadge domain="promotion" status={promotion.state} />
        </div>
        <div className="flex flex-wrap gap-3 type-subtext text-muted-foreground">
          {promotion.artifact_path && (
            <span className="font-mono">{promotion.artifact_path.split("/").slice(-1)[0]}</span>
          )}
          {savingsLabel && <span>saves {savingsLabel}</span>}
          {promotion.drift != null && (
            <span className={promotion.drift < 0.9 ? "text-destructive" : ""}>
              drift agreement {Math.round(promotion.drift * 100)}%
            </span>
          )}
        </div>
        {actions && <div className="flex flex-wrap gap-2 pt-1">{actions}</div>}
      </CardContent>
    </Card>
  );
}
